<template>
  <div class="advantage-page dark-panel">
    <section class="advantage-hero reveal">
      <div class="container container--move-left">
        <div class="advantage-hero__layout">
          <div class="advantage-hero__visual">
            <div class="advantage-hero__main ratio-4x3">
              <img :src="page.heroImage" alt="">
            </div>
            <div class="advantage-hero__pattern ratio-1x1">
              <img :src="page.heroPattern" alt="">
            </div>
          </div>
          <div class="advantage-hero__content">
            <div class="advantage-hero__headline">
              <p class="advantage-hero__eyebrow">{{ page.heroEyebrow }}</p>
              <h1 class="advantage-hero__title">
                {{ page.heroTitle }}
                <span>{{ page.heroTitleBold }}</span>
              </h1>
            </div>
            <p class="advantage-hero__intro">{{ page.heroIntro }}</p>
          </div>
        </div>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.strength'), to: '/strength' },
        { label: $t('nav.strength'), to: '/strength' }
      ]"
      :back-label="$t('common.backToHome')"
      back-to="/"
    />

    <section class="advantage-copy reveal">
      <div class="container container-narrow">
        <p class="advantage-copy__pretitle">{{ page.sectionPretitle }}</p>
        <h2 class="advantage-copy__title">{{ page.sectionTitle }}</h2>
        <p class="advantage-copy__lead">{{ page.sectionIntro }}</p>

        <div class="advantage-copy__list">
          <article
            v-for="item in page.items"
            :key="item.title"
            class="advantage-copy__item"
          >
            <h3>{{ item.title }}</h3>
            <p>{{ item.body }}</p>
          </article>
        </div>

        <p class="advantage-copy__outro">{{ page.outro }}</p>
      </div>
    </section>

    <section v-if="page.video" class="advantage-video reveal">
      <div class="container container-narrow">
        <h2 class="advantage-video__title">{{ page.videoTitle }}</h2>
        <div class="advantage-video__player">
          <video
            ref="strengthVideo"
            class="advantage-video__element"
            :class="{ 'is-playing': videoPlaying }"
            :poster="page.videoPoster"
            :src="page.video"
            controls
            playsinline
            muted
            @play="videoPlaying = true"
            @pause="onVideoPause"
          ></video>
          <div v-show="!videoPlaying" class="advantage-video__cover">
            <img :src="page.videoPoster" alt="">
            <button
              type="button"
              class="advantage-video__play"
              :aria-label="$t('common.playVideo')"
              @click="playVideo"
            >
              <span aria-hidden="true">▶</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'
import { fetchPublicPageSections, resolveAssetUrl } from '../api/public'

export default {
  name: 'StrengthPage',
  components: {
    PageTrail
  },
  data () {
    return {
      page: { ...site.strength },
      videoPlaying: false
    }
  },
  created () {
    fetchPublicPageSections({ pageKey: 'strength' })
      .then(sections => {
        if (Array.isArray(sections) && sections.length) {
          this.applyPageSections(sections)
        }
      })
      .catch(() => {
        this.page = { ...site.strength }
      })
  },
  methods: {
    applyPageSections (sections) {
      const page = { ...site.strength }
      const hero = sections.find(item => item.sectionKey === 'hero')
      const intro = sections.find(item => item.sectionKey === 'intro')
      const video = sections.find(item => item.sectionKey === 'video')

      if (hero) {
        page.heroTitle = hero.title || page.heroTitle
        page.heroIntro = hero.description || page.heroIntro
        page.heroImage = resolveAssetUrl(hero.image, page.heroImage)
      }

      if (intro) {
        page.sectionTitle = intro.title || page.sectionTitle
        page.sectionIntro = intro.description || page.sectionIntro
        if (Array.isArray(intro.imagesJson) && intro.imagesJson.length) {
          page.items = intro.imagesJson
            .map(item => ({
              title: item.title || '',
              body: item.description || item.body || ''
            }))
            .filter(item => item.title || item.body)
        }
      }

      if (video) {
        page.videoTitle = video.title || page.videoTitle
        page.videoPoster = resolveAssetUrl(video.image, page.videoPoster)
        page.video = video.description || page.video
      }

      this.page = page
    },
    playVideo () {
      const video = this.$refs.strengthVideo
      if (!video) return
      this.videoPlaying = true
      video.muted = false
      video.play().catch(() => {})
    },
    onVideoPause () {
      const video = this.$refs.strengthVideo
      if (!video) return
      if (video.currentTime === 0) {
        this.videoPlaying = false
      }
    }
  }
}
</script>

<style>
.advantage-page {
  color: #fff;
  background: #000;
}

.advantage-page .page-trail {
  background: #000;
  border-bottom-color: rgba(255, 255, 255, 0.14);
}

.advantage-page .page-trail__back,
.advantage-page .page-trail__crumb {
  color: rgba(255, 255, 255, 0.62);
}

.advantage-page .page-trail__back:hover,
.advantage-page .page-trail__crumb:hover {
  color: #fff;
}

.advantage-page .page-trail__crumb:not(:last-child)::after {
  color: rgba(255, 255, 255, 0.32);
}

.advantage-page .page-trail__crumb--current {
  color: #fff;
}

.advantage-page .container--move-left {
  width: min(calc(800px + 50vw + 26px), 100%);
  margin-right: auto;
  margin-left: 0;
  padding-right: 35px;
  padding-left: 0;
}

.advantage-page .container-narrow {
  width: min(1127px, calc(100vw - 48px));
  margin: 0 auto;
}

.ratio-4x3 {
  aspect-ratio: 4 / 3;
}

.ratio-1x1 {
  aspect-ratio: 1 / 1;
}

.advantage-hero {
  padding: clamp(56px, 7vw, 88px) 0 clamp(32px, 4vw, 48px);
}

.advantage-hero__layout {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
  gap: clamp(24px, 3vw, 40px);
  align-items: stretch;
}

.advantage-hero__visual {
  position: relative;
}

.advantage-hero__main {
  overflow: hidden;
  background: #e8e2d9;
}

.advantage-hero__main img,
.advantage-hero__pattern img,
.advantage-video__cover img,
.advantage-video__element {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.advantage-hero__pattern {
  position: absolute;
  right: -5rem;
  bottom: -4.375rem;
  width: 38%;
  overflow: hidden;
  //background: #d9d4ca;
  //box-shadow: 0 0 40px rgba(0, 0, 0, 0.75);
  box-shadow: 0 0 40px #323232;
  z-index: 1;
}

.advantage-hero__content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 100%;
  margin-top: 2.8125rem;
  margin-left: 0;
  padding: 0 clamp(20px, 3vw, 48px) 0 0;
}

.advantage-hero__headline {
  position: relative;
  z-index: 1;
}

.advantage-hero__eyebrow {
  margin: 0 0 0.25rem;
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.72);
}

.advantage-hero__title {
  margin: 0.25rem 0 0;
  font-size: clamp(2.8125rem, 0.8rem + 4.2vw, 5rem);
  font-weight: 400;
  line-height: 1.18;
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
}

.advantage-hero__title span {
  font-weight: 700;
}

.advantage-hero__intro {
  margin: 1rem 0 0;
  padding-left: 3.125rem;
  max-width: none;
  font-size: clamp(15px, 1.4vw, 17px);
  line-height: 2;
  color: rgba(255, 255, 255, 0.82);
}

@media (min-width: 992px) {
  .advantage-hero__content {
    margin-top: 0;
    margin-left: -23%;
    padding-top: 35px;
  }

  .advantage-hero__title {
    font-size: clamp(2.8125rem, 0.8rem + 4.2vw, 6.25rem);
    letter-spacing: 0.125rem;
    line-height: 1.17;
  }

  .advantage-hero__intro {
    min-height: 180px;
    padding-left: 20%;
  }
}

@media (min-width: 1200px) {
  .advantage-hero__intro {
    min-height: 220px;
    padding-left: 32%;
  }
}

@media (min-width: 1600px) {
  .advantage-hero__content {
    padding-top: 15px;
  }

  .advantage-hero__intro {
    min-height: 290px;
  }
}

.advantage-copy {
  padding: clamp(40px, 5vw, 56px) 0 clamp(56px, 6vw, 80px);
}

.advantage-copy__pretitle {
  margin: 0 0 12px;
  font-size: 15px;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.62);
}

.advantage-copy__title {
  margin: 0 0 24px;
  font-size: clamp(30px, 2.8vw, 44px);
  font-weight: 400;
  line-height: 1.1;
}

.advantage-copy__lead,
.advantage-copy__outro {
  margin: 0 0 28px;
  font-size: 16px;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.82);
}

.advantage-copy__outro {
  margin-bottom: 0;
}

.advantage-copy__list {
  margin: 36px 0 32px;
  padding-left: calc(5rem + 50px);
  border-left: none;
  position: relative;
}

.advantage-copy__list::before {
  position: absolute;
  top: 0.625rem;
  left: 0;
  width: 5rem;
  height: 1px;
  background: rgba(255, 255, 255, 0.35);
  content: "";
}

.advantage-copy__item {
  position: relative;
  padding-left: 0;
}

.advantage-copy__item + .advantage-copy__item {
  margin-top: 28px;
}

.advantage-copy__item::before {
  position: absolute;
  top: 0.925rem;
  left: calc(-5rem - 50px);
  width: 5rem;
  height: 1px;
  background: rgba(255, 255, 255, 0.35);
  content: "";
}

.advantage-copy__item h3 {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
}

.advantage-copy__item p {
  margin: 0;
  font-size: 16px;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.82);
}

.advantage-video {
  padding: 0 0 clamp(80px, 9vw, 120px);
}

.advantage-video__title {
  margin: 0 0 32px;
  font-size: clamp(30px, 2.8vw, 44px);
  font-weight: 400;
  line-height: 1.1;
}

.advantage-video__player {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #171411;
}

.advantage-video__element {
  position: relative;
  z-index: 2;
}

.advantage-video__element:not(.is-playing) {
  opacity: 0;
  pointer-events: none;
}

.advantage-video__cover {
  position: absolute;
  inset: 0;
  z-index: 3;
}

.advantage-video__play {
  position: absolute;
  top: 50%;
  left: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  min-height: 72px;
  padding: 0.675rem 1.25rem;
  border: 0;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 24px;
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.advantage-video__play span {
  margin-left: 4px;
}

@media (max-width: 1024px) {
  .advantage-hero__layout {
    grid-template-columns: 1fr;
  }

  .advantage-hero__content {
    margin-left: auto;
    margin-right: auto;
    margin-top: 5rem;
    padding: 0 2.1875rem;
    max-width: 720px;
  }

  .advantage-hero__intro {
    margin-left: 0;
    padding-left: 0;
    min-height: auto;
  }

  .advantage-hero__pattern {
    right: 2.1875rem;
    bottom: -4.375rem;
    width: 38%;
  }
}

@media (max-width: 767px) {
  .advantage-page .container--move-left {
    width: calc(100vw - 25px);
    padding-right: 25px;
  }

  .advantage-hero__content {
    margin-top: 5rem;
    padding-left: 0;
    padding-right: 0;
    max-width: none;
  }

  .advantage-hero__title {
    font-size: 2.2125rem;
  }

  .advantage-hero__pattern {
    right: 0;
    bottom: -2.125rem;
    width: 45%;
  }

  .advantage-copy__list {
    padding-left: calc(1rem + 10px);
  }

  .advantage-copy__list::before {
    width: 1rem;
  }

  .advantage-copy__item::before {
    left: calc(-1rem - 10px);
    width: 1rem;
    top: 1.025rem;
  }

  .advantage-hero__eyebrow,
  .advantage-hero__intro,
  .advantage-copy__item p,
  .advantage-copy__lead,
  .advantage-copy__outro {
    font-size: 14px;
    line-height: 2;
  }

  .advantage-copy__item h3 {
    font-size: 16px;
  }
}
</style>
